import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DynamicFormRenderer, createTempAttachmentUploadAdapter } from '@scientist/dynamic-form-renderer';
import { SimpleWysiwygRichText } from '@scientist/dynamic-form-renderer/richtext-simple';
import { DownshiftMultiSelect } from '@scientist/dynamic-form-renderer/enhanced-select';
import ChemDoodleWidget from '../widgets/ChemDoodleWidget.jsx';
import { registerForm, unregisterForm } from '../../lib/dynamicFormRegistry.js';

const widgetComponents = { chem_doodle: ChemDoodleWidget };

// Debounce interval for mirroring the live form value into the hidden input.
// Large forms (deep/repeatable groups) make per-keystroke JSON.stringify costly,
// so we coalesce writes; submit flushes synchronously regardless.
const SYNC_DEBOUNCE_MS = 150;

// Mirrors fieldIdFor in the renderer package so we can locate a field's
// control element (e.g. to scroll the first validation error into view).
function fieldElementId(path) {
  return `dff-${path.replace(/[^a-zA-Z0-9_-]+/g, '-')}`;
}

// Edit-mode wrapper for embedding DynamicFormRenderer inside a host-page form,
// replacing the legacy Alpaca embed (shared/_alpaca_form_init). Like Alpaca it
// renders form content only — no <form> element or submit button. Instead it:
//
// - listens (document-level, capture phase) for clicks on the host page's
//   submit button(s) matching `submitButtonSelector`
// - on click, validates; if invalid it blocks the submission, scrolls/focuses
//   the first error, and alerts (legacy parity). When `validateOnSubmit` is
//   false this gating is skipped entirely: the value is flushed and the
//   submission proceeds without client-side validation or blocking.
// - if valid, JSON-encodes the form value into the sibling hidden input
//   (`dataInputId`) so the surrounding form submits it unchanged
// - dispatches `df:rendered` on mount, which legacy listeners use to enable
//   submit buttons (e.g. storefront quote_groups.js)
//
// It also keeps `dataInputId` in sync with the live form value (debounced, via
// the renderer's onChange) so developers can inspect in-progress data and other
// page JS can read it. This mirrors unvalidated/in-progress state by design;
// submission is still blocked while invalid, so the server never sees bad data.
//
// Capture phase matters: it runs before bubble-phase handlers and the native
// submit, so preventDefault reliably blocks submission. We use stopPropagation
// (not stopImmediatePropagation) so sibling embeds sharing the same submit
// button still validate and display their own errors.
export default function DynamicFormEmbed({
  definition,
  defaultValue,
  dataInputId,
  submitButtonSelector,
  enableRichText = false,
  validateOnSubmit = true,
}) {
  const handleRef = useRef(null);
  const containerRef = useRef(null);
  const syncTimerRef = useRef(null);

  // Writes a value into the hidden input now, cancelling any pending debounce.
  const flushValue = useCallback((value) => {
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    const dataInput = document.getElementById(dataInputId);
    if (dataInput) dataInput.value = JSON.stringify(value);
  }, [dataInputId]);

  // Debounced mirror of the live form value into the hidden input. Coalesces
  // bursts of edits into a single write so large forms stay responsive.
  const syncValue = useCallback(() => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      syncTimerRef.current = null;
      if (handleRef.current) flushValue(handleRef.current.getValue());
    }, SYNC_DEBOUNCE_MS);
  }, [flushValue]);

  useEffect(() => () => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
  }, []);

  // Persists upload-field files to Rails' /temp_attachments; they're promoted
  // to permanent attachments after submit by Pg::HasDynamicForms.
  const uploadAdapter = useMemo(
    () => createTempAttachmentUploadAdapter({
      csrfToken: document.querySelector('meta[name="csrf-token"]')?.content || '',
    }),
    [],
  );

  useEffect(() => {
    const handle = handleRef.current;
    // Publish the live handle so feature code can read form state by id
    // (registry import or inline off the event detail). The handle is an
    // in-memory ref — don't JSON.stringify the event detail.
    registerForm(dataInputId, handle);
    document.dispatchEvent(new CustomEvent('df:rendered', {
      bubbles: true,
      detail: { formId: dataInputId, handle },
    }));
    // onChange doesn't fire on mount, so seed the hidden input with the
    // renderer's initial value (incl. defaults) right away.
    if (handle) flushValue(handle.getValue());
    return () => unregisterForm(dataInputId);
  }, [dataInputId, flushValue]);

  useEffect(() => {
    const onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest(submitButtonSelector)) return;

      const handle = handleRef.current;
      if (!handle) return;

      // Opt-out: skip validation/blocking, just flush the latest value so the
      // surrounding form submits it as-is.
      if (!validateOnSubmit) {
        flushValue(handle.getValue());
        return;
      }

      const errors = handle.validate();
      const firstErrorPath = Object.keys(errors)[0];

      if (firstErrorPath) {
        event.preventDefault();
        event.stopPropagation();

        const errorEl = containerRef.current?.querySelector(`#${CSS.escape(fieldElementId(firstErrorPath))}`);
        errorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        handle.focusField(firstErrorPath);
        window.alert('Please correct the errors above before continuing.');
      } else {
        flushValue(handle.getValue());
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [submitButtonSelector, flushValue, validateOnSubmit]);

  return (
    <div ref={containerRef}>
      <DynamicFormRenderer
        ref={handleRef}
        definition={definition}
        defaultValue={defaultValue}
        onChange={syncValue}
        mode="edit"
        enableRichText={enableRichText}
        richTextAdapter={SimpleWysiwygRichText}
        enhancedSelectAdapter={DownshiftMultiSelect}
        widgetComponents={widgetComponents}
        uploadAdapter={uploadAdapter}
      />
    </div>
  );
}
